import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  PlusIcon,
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  UserIcon,
  AcademicCapIcon,
  XMarkIcon,
  PencilIcon,
  TrashIcon,
  CheckIcon,
} from '@heroicons/react/24/outline';
import { useOrgCharts } from '@/hooks';
import { useSchool } from '@/contexts/SchoolContext';
import { useAuth } from '@/features/auth/hooks';
import { hasAuthoritativeSchoolAdminAccess } from '@/services/domain/schoolMembership';
import orgChartService from '@/services/orgChartService';
import { ROUTES } from '@/utils/constants';
import { ConfirmDialog } from '@/components/ui';
import { useToast } from '@/components/feedback/ToastSystem';
import LoadingSpinner from '@/components/ui/LoadingSpinner';

/**
 * OrgChartPanel — substrate slide-out for organisation charts.
 *
 * Real ops inline:
 *   - switch / create / delete chart
 *   - add child position under any node
 *   - rename, delete a position
 *   - remove staff or class assignment with one click
 * Full staff/class assignment pickers stay at /org-chart.
 */

const flattenTree = (nodes) => {
  if (!nodes?.length) return [];
  const byParent = new Map();
  nodes.forEach((n) => {
    const key = n.parentId || '__root__';
    if (!byParent.has(key)) byParent.set(key, []);
    byParent.get(key).push(n);
  });
  byParent.forEach((arr) => arr.sort((a, b) => (a.order || 0) - (b.order || 0)));
  const out = [];
  const walk = (parentKey, depth) => {
    (byParent.get(parentKey) || []).forEach((n) => {
      out.push({ ...n, depth, hasChildren: byParent.has(n.id) });
      walk(n.id, depth + 1);
    });
  };
  walk('__root__', 0);
  return out;
};

export default function OrgChartPanel() {
  const { schoolId } = useSchool();
  const { user } = useAuth();
  const canManage = hasAuthoritativeSchoolAdminAccess(user, schoolId);
  const {
    orgCharts,
    loading,
    createOrgChart,
    deleteOrgChart,
    savePositions,
    generatePositionId,
    refresh,
  } = useOrgCharts();
  const { showSuccess, showError } = useToast();

  const [activeChartId, setActiveChartId] = useState(null);
  const [showCreateChart, setShowCreateChart] = useState(false);
  const [newChartName, setNewChartName] = useState('');
  const [creatingChart, setCreatingChart] = useState(false);

  const [expandedPositions, setExpandedPositions] = useState(() => new Set());
  const [addingChildOf, setAddingChildOf] = useState(null);
  const [newPositionLabel, setNewPositionLabel] = useState('');
  const [savingPosition, setSavingPosition] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteChartTarget, setDeleteChartTarget] = useState(null);
  const newPositionInputRef = useRef(null);
  const renameInputRef = useRef(null);

  const activeChart = useMemo(() => {
    if (!orgCharts.length) return null;
    return orgCharts.find((c) => c.id === activeChartId) || orgCharts[0];
  }, [orgCharts, activeChartId]);

  const tree = useMemo(() => flattenTree(activeChart?.nodes || []), [activeChart]);

  useEffect(() => {
    if (addingChildOf && newPositionInputRef.current) {
      newPositionInputRef.current.focus();
    }
  }, [addingChildOf]);

  useEffect(() => {
    if (renamingId && renameInputRef.current) {
      renameInputRef.current.focus();
      renameInputRef.current.select();
    }
  }, [renamingId]);

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <LoadingSpinner />
      </div>
    );
  }

  const togglePosition = (id) => {
    setExpandedPositions((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleCreateChart = async () => {
    const name = newChartName.trim();
    if (!name) return;
    setCreatingChart(true);
    try {
      await createOrgChart(name);
      const created = orgCharts.find((c) => c.name === name);
      if (created?.id) setActiveChartId(created.id);
      setNewChartName('');
      setShowCreateChart(false);
      showSuccess('Chart created', '', 2500);
    } catch (err) {
      showError('Could not create chart', err.message || '');
    } finally {
      setCreatingChart(false);
    }
  };

  const handleDeleteChart = async () => {
    if (!deleteChartTarget) return;
    try {
      await deleteOrgChart(deleteChartTarget.id);
      if (activeChartId === deleteChartTarget.id) setActiveChartId(null);
      showSuccess('Chart deleted', '', 2500);
    } catch (err) {
      showError('Could not delete chart', err.message || '');
    } finally {
      setDeleteChartTarget(null);
    }
  };

  const handleAddPosition = async (parentId) => {
    const label = newPositionLabel.trim();
    if (!label || savingPosition || !activeChart) return;
    setSavingPosition(true);
    try {
      const newPosition = {
        id: generatePositionId(),
        label,
        type: 'role',
        refId: null,
        parentId: parentId || null,
        order: (activeChart.nodes?.length || 0) + 1,
        staffCards: [],
        classCards: [],
        gradeCards: [],
        subjectCards: [],
      };
      await savePositions(activeChart.id, [...(activeChart.nodes || []), newPosition]);
      if (parentId) {
        setExpandedPositions((prev) => new Set(prev).add(parentId));
      }
      setNewPositionLabel('');
      setAddingChildOf(null);
      showSuccess(`${label} added`, '', 2000);
    } catch (err) {
      showError('Could not add position', err.message || '');
    } finally {
      setSavingPosition(false);
    }
  };

  const handleRename = async (position) => {
    const label = renameValue.trim();
    if (!label || label === position.label) {
      setRenamingId(null);
      return;
    }
    try {
      const updated = activeChart.nodes.map((p) =>
        p.id === position.id ? { ...p, label } : p
      );
      await savePositions(activeChart.id, updated);
      setRenamingId(null);
    } catch (err) {
      showError('Could not rename', err.message || '');
    }
  };

  const handleDeletePosition = async () => {
    if (!deleteTarget || !activeChart) return;
    const toRemove = new Set([deleteTarget.id]);
    let changed = true;
    while (changed) {
      changed = false;
      activeChart.nodes.forEach((p) => {
        if (p.parentId && toRemove.has(p.parentId) && !toRemove.has(p.id)) {
          toRemove.add(p.id);
          changed = true;
        }
      });
    }
    const remaining = activeChart.nodes.filter((p) => !toRemove.has(p.id));
    try {
      await savePositions(activeChart.id, remaining);
      showSuccess('Position removed', '', 2000);
    } catch (err) {
      showError('Could not remove', err.message || '');
    } finally {
      setDeleteTarget(null);
    }
  };

  const handleRemoveCard = async (positionId, cardType, cardKey) => {
    if (!activeChart) return;
    const updated = orgChartService.removeCard(
      activeChart.nodes,
      positionId,
      cardType,
      cardKey
    );
    try {
      await savePositions(activeChart.id, updated);
      refresh?.();
    } catch (err) {
      showError('Could not unassign', err.message || '');
    }
  };

  return (
    <div className="p-4 pb-24 space-y-3">
      <div className="flex items-center justify-between gap-2">
        {orgCharts.length > 1 ? (
          <select
            className="select select-bordered select-xs flex-1 min-w-0"
            value={activeChart?.id || ''}
            onChange={(e) => { setActiveChartId(e.target.value); setExpandedPositions(new Set()); }}
            aria-label="Select chart"
          >
            {orgCharts.map((c) => (
              <option key={c.id} value={c.id}>{c.name || 'Untitled'}</option>
            ))}
          </select>
        ) : (
          <h3 className="text-sm font-semibold text-base-content/70 truncate">
            {activeChart?.name || 'Org chart'}
          </h3>
        )}
        <div className="flex items-center gap-1">
          {canManage && activeChart && (
            <button
              type="button"
              className="btn btn-ghost btn-xs btn-square min-h-0 h-6 w-6 text-base-content/50 hover:text-error"
              onClick={() => setDeleteChartTarget(activeChart)}
              title="Delete chart"
              aria-label="Delete chart"
            >
              <TrashIcon className="w-3.5 h-3.5" />
            </button>
          )}
          {canManage && !showCreateChart && (
            <button
              type="button"
              className="btn btn-ghost btn-xs gap-1"
              onClick={() => setShowCreateChart(true)}
              title="New chart"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Chart
            </button>
          )}
        </div>
      </div>

      {canManage && showCreateChart && (
        <div className="card bg-base-100 border border-base-300 p-3 space-y-2">
          <input
            type="text"
            className="input input-xs input-bordered h-7 w-full"
            placeholder="Chart name"
            value={newChartName}
            onChange={(e) => setNewChartName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateChart();
              if (e.key === 'Escape') { setShowCreateChart(false); setNewChartName(''); }
            }}
            autoFocus
          />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              className="btn btn-ghost btn-xs"
              onClick={() => { setShowCreateChart(false); setNewChartName(''); }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary btn-xs"
              onClick={handleCreateChart}
              disabled={!newChartName.trim() || creatingChart}
            >
              {creatingChart ? 'Creating…' : 'Create'}
            </button>
          </div>
        </div>
      )}

      {!activeChart && (
        <p className="text-sm text-base-content/50 py-6 text-center">
          No org charts yet.
        </p>
      )}

      {activeChart && (
        <div className="text-xs text-base-content/50 flex items-center justify-between">
          <span>{tree.length} position{tree.length === 1 ? '' : 's'}</span>
          {canManage && <Link
            to={`${ROUTES.ORG_CHART}?chart=${activeChart.id}`}
            className="link link-hover inline-flex items-center gap-1"
          >
            <ArrowTopRightOnSquareIcon className="w-3 h-3" />
            Open chart
          </Link>}
        </div>
      )}

      {activeChart && tree.length === 0 && addingChildOf !== null && (
        <p className="text-sm text-base-content/50 py-2 text-center">
          No positions yet — add one below.
        </p>
      )}

      {activeChart && (
        <div className="space-y-1">
          {tree.map((node) => {
            const isExpanded = expandedPositions.has(node.id);
            const isAddingHere = addingChildOf === node.id;
            const isRenamingHere = renamingId === node.id;
            const staffCount = node.staffCards?.length || 0;
            const classCount = node.classCards?.length || 0;
            const reviewsMarks = node.responsibilities?.includes('assessmentReview');
            const hasDetail = staffCount > 0 || classCount > 0 || node.subjectCards?.length > 0 || node.gradeCards?.length > 0 || reviewsMarks;
            return (
              <div key={node.id}>
                <div
                  style={{ paddingLeft: `${0.25 + node.depth * 0.875}rem` }}
                  className={`group flex items-center gap-1 pr-1 rounded
                    ${isExpanded ? 'bg-base-200' : 'hover:bg-base-200'}`}
                >
                  <button
                    type="button"
                    onClick={() => hasDetail && togglePosition(node.id)}
                    disabled={!hasDetail}
                    className={`flex items-center gap-1 py-1.5 flex-none ${hasDetail ? 'cursor-pointer' : 'cursor-default'}`}
                    aria-expanded={hasDetail ? isExpanded : undefined}
                    aria-label={hasDetail ? 'Toggle assignments' : undefined}
                  >
                    {hasDetail ? (
                      isExpanded ? (
                        <ChevronDownIcon className="w-3 h-3 text-base-content/40" />
                      ) : (
                        <ChevronRightIcon className="w-3 h-3 text-base-content/40" />
                      )
                    ) : (
                      <span className="w-3" />
                    )}
                  </button>
                  {isRenamingHere ? (
                    <input
                      ref={renameInputRef}
                      type="text"
                      value={renameValue}
                      onChange={(e) => setRenameValue(e.target.value)}
                      onBlur={() => handleRename(node)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleRename(node);
                        if (e.key === 'Escape') setRenamingId(null);
                      }}
                      className="input input-xs input-bordered flex-1 h-7"
                      maxLength={60}
                    />
                  ) : (
                    <button
                      type="button"
                      onClick={() => hasDetail && togglePosition(node.id)}
                      className="flex-1 min-w-0 truncate text-left text-sm py-1.5"
                    >
                      {node.label || 'Untitled'}
                    </button>
                  )}
                  {!isRenamingHere && (
                    <>
                      {staffCount > 0 && (
                        <span className="badge badge-ghost badge-xs gap-0.5">
                          <UserIcon className="w-2.5 h-2.5" />
                          {staffCount}
                        </span>
                      )}
                      {classCount > 0 && (
                        <span className="badge badge-ghost badge-xs gap-0.5">
                          <AcademicCapIcon className="w-2.5 h-2.5" />
                          {classCount}
                        </span>
                      )}
                      {canManage && <div className="opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition-opacity flex items-center">
                        <button
                          type="button"
                          onClick={() => { setAddingChildOf(node.id); setNewPositionLabel(''); }}
                          className="btn btn-ghost btn-xs btn-square min-h-0 h-6 w-6 text-base-content/50 hover:text-primary"
                          title="Add child"
                          aria-label="Add child position"
                        >
                          <PlusIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                          type="button"
                          onClick={() => { setRenamingId(node.id); setRenameValue(node.label || ''); }}
                          className="btn btn-ghost btn-xs btn-square min-h-0 h-6 w-6 text-base-content/50 hover:text-primary"
                          title="Rename"
                          aria-label="Rename position"
                        >
                          <PencilIcon className="w-3 h-3" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setDeleteTarget(node)}
                          className="btn btn-ghost btn-xs btn-square min-h-0 h-6 w-6 text-base-content/50 hover:text-error"
                          title="Delete"
                          aria-label="Delete position"
                        >
                          <TrashIcon className="w-3 h-3" />
                        </button>
                      </div>}
                    </>
                  )}
                </div>

                {isAddingHere && (
                  <div
                    style={{ paddingLeft: `${1.25 + node.depth * 0.875}rem` }}
                    className="py-1 pr-1 flex items-center gap-1"
                  >
                    <input
                      ref={newPositionInputRef}
                      type="text"
                      value={newPositionLabel}
                      onChange={(e) => setNewPositionLabel(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddPosition(node.id);
                        if (e.key === 'Escape') { setAddingChildOf(null); setNewPositionLabel(''); }
                      }}
                      placeholder="New position name…"
                      className="input input-xs input-bordered flex-1 h-7"
                      maxLength={60}
                      disabled={savingPosition}
                    />
                    <button
                      type="button"
                      onClick={() => handleAddPosition(node.id)}
                      disabled={!newPositionLabel.trim() || savingPosition}
                      className="btn btn-primary btn-xs btn-square min-h-0 h-7 w-7"
                      aria-label="Save"
                    >
                      <CheckIcon className="w-3.5 h-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => { setAddingChildOf(null); setNewPositionLabel(''); }}
                      className="btn btn-ghost btn-xs btn-square min-h-0 h-7 w-7"
                      aria-label="Cancel"
                    >
                      <XMarkIcon className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}

                {isExpanded && hasDetail && (
                  <div
                    style={{ paddingLeft: `${1.75 + node.depth * 0.875}rem` }}
                    className="py-1 pr-1 space-y-1 text-xs text-base-content/60"
                  >
                    {reviewsMarks && <p className="font-medium px-1">HOD marks review</p>}
                    {node.subjectCards?.length > 0 && <p className="px-1">Subjects: {node.subjectCards.map(subject => subject.subjectName || subject.subjectId).join(', ')}</p>}
                    {node.gradeCards?.length > 0 && <p className="px-1">Grades: {node.gradeCards.map(grade => grade.gradeName || grade.gradeId).join(', ')}</p>}
                    {node.staffCards?.map((s) => (
                      <div key={`s-${s.uid}`} className="group/card flex items-center gap-1.5 hover:bg-base-200 rounded px-1 py-0.5">
                        <UserIcon className="w-3 h-3 flex-none text-base-content/40" />
                        <span className="flex-1 min-w-0 truncate">{s.displayName || s.email || 'Unknown'}</span>
                        {canManage && <button
                          type="button"
                          onClick={() => handleRemoveCard(node.id, 'staffCards', s.uid)}
                          className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 text-base-content/40 hover:text-error opacity-0 group-hover/card:opacity-100"
                          aria-label={`Unassign ${s.displayName}`}
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>}
                      </div>
                    ))}
                    {node.classCards?.map((c) => (
                      <div key={`c-${c.classId}`} className="group/card flex items-center gap-1.5 hover:bg-base-200 rounded px-1 py-0.5">
                        <AcademicCapIcon className="w-3 h-3 flex-none text-base-content/40" />
                        <span className="flex-1 min-w-0 truncate">{c.name || c.className || 'Class'}</span>
                        {node.homeroomClassId === c.classId && (
                          <span className="badge badge-primary badge-xs">homeroom</span>
                        )}
                        {canManage && <button
                          type="button"
                          onClick={() => handleRemoveCard(node.id, 'classCards', c.classId)}
                          className="btn btn-ghost btn-xs btn-square min-h-0 h-5 w-5 text-base-content/40 hover:text-error opacity-0 group-hover/card:opacity-100"
                          aria-label={`Unassign ${c.name || c.className || 'class'}`}
                        >
                          <XMarkIcon className="w-3 h-3" />
                        </button>}
                      </div>
                    ))}
                    {canManage && <Link
                      to={`${ROUTES.ORG_CHART}?chart=${activeChart.id}&position=${node.id}`}
                      className="inline-flex items-center gap-1 text-base-content/50 hover:text-primary px-1"
                    >
                      <ArrowTopRightOnSquareIcon className="w-3 h-3" />
                      Assign people / classes
                    </Link>}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add root position */}
          {canManage && (addingChildOf === '__root__' ? (
            <div className="py-1 pr-1 flex items-center gap-1">
              <input
                ref={newPositionInputRef}
                type="text"
                value={newPositionLabel}
                onChange={(e) => setNewPositionLabel(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleAddPosition(null);
                  if (e.key === 'Escape') { setAddingChildOf(null); setNewPositionLabel(''); }
                }}
                placeholder="New top-level position…"
                className="input input-xs input-bordered flex-1 h-7"
                maxLength={60}
                disabled={savingPosition}
              />
              <button
                type="button"
                onClick={() => handleAddPosition(null)}
                disabled={!newPositionLabel.trim() || savingPosition}
                className="btn btn-primary btn-xs btn-square min-h-0 h-7 w-7"
                aria-label="Save"
              >
                <CheckIcon className="w-3.5 h-3.5" />
              </button>
              <button
                type="button"
                onClick={() => { setAddingChildOf(null); setNewPositionLabel(''); }}
                className="btn btn-ghost btn-xs btn-square min-h-0 h-7 w-7"
                aria-label="Cancel"
              >
                <XMarkIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => { setAddingChildOf('__root__'); setNewPositionLabel(''); }}
              className="w-full flex items-center gap-1.5 px-2 py-1.5 mt-1 rounded text-left text-sm text-base-content/60 hover:text-primary hover:bg-base-200"
            >
              <PlusIcon className="w-3.5 h-3.5" />
              Add position
            </button>
          ))}
        </div>
      )}

      {deleteTarget && (
        <ConfirmDialog
          isOpen
          onClose={() => setDeleteTarget(null)}
          onConfirm={handleDeletePosition}
          title={`Remove "${deleteTarget.label}"?`}
          message="Sub-positions and assignments under this position will also be removed."
          confirmText="Remove"
          variant="danger"
        />
      )}

      {deleteChartTarget && (
        <ConfirmDialog
          isOpen
          onClose={() => setDeleteChartTarget(null)}
          onConfirm={handleDeleteChart}
          title={`Delete "${deleteChartTarget.name}"?`}
          message="All positions and assignments in this chart will be removed."
          confirmText="Delete chart"
          variant="danger"
          confirmTextMatch={deleteChartTarget.name}
        />
      )}
    </div>
  );
}
